import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export function useOrganizationTheme() {
  // Check if user is authenticated by looking for auth-related data
  const isAuthenticated = () => {
    // Check if there's a session or auth token
    return (
      document.cookie.includes("techalign.session") ||
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token")
    );
  };

  const { data: organization } = useQuery<any>({
    queryKey: ["/api/organizations/current"],
    queryFn: () => apiRequest("GET", "/api/organizations/current"),
    enabled: !!isAuthenticated(), // Convert to boolean and only make the API call if user is authenticated
    retry: false, // Don't retry on 401 errors
  });

  // Function to convert hex color to HSL
  const hexToHsl = (hex: string) => {
    // Remove # if present
    hex = hex.replace("#", "");

    // Convert hex to RGB
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  };

  // Function to apply primary color to CSS variables
  const applyPrimaryColor = (color: string) => {
    const { h, s, l } = hexToHsl(color);
    const root = document.documentElement;

    // Update CSS variables for organization-specific theming
    root.style.setProperty("--org-primary", `hsl(${h}, ${s}%, ${l}%)`);
    root.style.setProperty("--org-ring", `hsl(${h}, ${s}%, ${l}%)`);

    // For dark mode, we might want a slightly lighter version
    const darkL = Math.min(l + 10, 90);
    root.style.setProperty("--org-primary-dark", `hsl(${h}, ${s}%, ${darkL}%)`);

    // Apply to actual CSS variables used by components
    root.style.setProperty(
      "--primary",
      `var(--org-primary, hsl(221.2, 83.2%, 53.3%))`,
    );
    root.style.setProperty(
      "--ring",
      `var(--org-ring, hsl(221.2, 83.2%, 53.3%))`,
    );
  };

  // Apply organization theme when organization data changes
  useEffect(() => {
    if (organization?.settings?.primaryColor) {
      applyPrimaryColor(organization.settings.primaryColor);
    } else {
      // Reset to default colors when no organization color is set
      const root = document.documentElement;
      root.style.removeProperty("--org-primary");
      root.style.removeProperty("--org-ring");
      root.style.removeProperty("--org-primary-dark");
      root.style.setProperty("--primary", "hsl(221.2, 83.2%, 53.3%)");
      root.style.setProperty("--ring", "hsl(221.2, 83.2%, 53.3%)");
    }
  }, [organization?.settings?.primaryColor]);

  return {
    organization,
    applyPrimaryColor,
  };
}
